import { Paper, Group, Text, ThemeIcon, Badge, Box } from '@mantine/core';
import { IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';
import type { ReactNode } from 'react';

interface StatCardProps {
  title: string;
  value: string;
  trend?: number;
  icon: ReactNode;
  variant?: 'default' | 'gradient' | 'outlined';
}

export default function StatCard({
  title,
  value,
  trend,
  icon,
  variant = 'default',
}: StatCardProps) {
  const trendColor = trend && trend >= 0 ? 'brand' : 'red';
  const isPositive = trend !== undefined && trend >= 0;

  return (
    <Paper
      p="lg"
      shadow="sm"
      style={{
        borderLeft: variant === 'default' ? '3px solid var(--mantine-color-brand-5)' : undefined,
        background:
          variant === 'gradient'
            ? 'linear-gradient(135deg, var(--mantine-color-brand-0) 0%, var(--mantine-color-white) 100%)'
            : undefined,
        border: variant === 'outlined' ? '1px solid var(--mantine-color-gray-3)' : undefined,
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        cursor: 'default',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)';
      }}
    >
      <Group justify="space-between">
        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.04em' }}>
            {title}
          </Text>
          <Text fw={800} size="1.75rem" mt={8} lh={1.2}>
            {value}
          </Text>
          {trend !== undefined && (
            <Badge
              mt={8}
              variant="light"
              color={trendColor}
              size="sm"
              leftSection={
                isPositive ? (
                  <IconTrendingUp size={12} />
                ) : (
                  <IconTrendingDown size={12} />
                )
              }
            >
              {isPositive ? '+' : ''}
              {trend.toFixed(1)}%
            </Badge>
          )}
        </Box>
        <ThemeIcon
          variant="light"
          size={52}
          radius="xl"
          style={{ flexShrink: 0 }}
        >
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}
